/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2024-2026 (original work) Open Assessment Technologies SA;
 */
import { writable, get } from 'svelte/store';
import { sessionStorage } from '../core/utils/storage';

/**
 * Creates diagnostic settings store
 * Settings there are related to a specific computer in school (e.g. ip address and choice of FLA assessment)
 * @returns {Object}
 */
function createDiagnosticSettingsStore() {
    const initialValues = {
        isRunningAudioTest: false,
        isRunningVideoTest: false,
        ipAddress: '127.0.0.1'
    };
    const diagnosticSettingsValues = writable(initialValues);

    return {
        subscribe: diagnosticSettingsValues.subscribe,
        async updateValue(key, newValue) {
            diagnosticSettingsValues.update(s => ({
                ...s,
                [key]: newValue
            }));
            await this.storeValues();
        },
        async storeValues() {
            const storage = await sessionStorage;
            storage.setItem('diagnosticSettings', JSON.stringify(get(diagnosticSettingsValues)));
        },
        async restoreValues() {
            const storage = await sessionStorage;

            let values = {};
            try {
                const unparsedValues = await storage.getItem('diagnosticSettings');
                const parsedValues = JSON.parse(unparsedValues);
                values = parsedValues && typeof parsedValues === 'object' && !Array.isArray(parsedValues)
                    ? parsedValues
                    : {};
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Couldn\'t parse school settings values from session storage');
            } finally {
                diagnosticSettingsValues.set({
                    ...initialValues,
                    ...values
                });
            }
        },
        updateIpAddress(value) {
            return this.updateValue('ipAddress', value);
        },
        updateAudioTestSetting(value) {
            return this.updateValue('isRunningAudioTest', value);
        },
        updateVideoTestSetting(value) {
            return this.updateValue('isRunningVideoTest', value);
        }
    }
}

export default createDiagnosticSettingsStore();
